import type { ReactNode } from 'react'
import { cn } from '@/shared/lib'
import { Tabs } from '@/shared/ui/tabs'

export interface EntityPageTab<V extends string> {
  value: V
  label: string
  content: ReactNode
  badge?: ReactNode
}

interface EntityPageLayoutProps<V extends string> {
  header: ReactNode
  tabs: EntityPageTab<V>[]
  activeTab: V
  onTabChange: (tab: V) => void
  /** aria-label таб-листа («Разделы проекта»). */
  tabsLabel?: string
  /** Правый рельс (xl). Нет — экран одноколоночный. */
  rail?: ReactNode
  railTitle?: string
}

// Каркас страницы сущности: карточка-шапка сверху, секция с табами ниже,
// опциональный правый рельс на xl. Намеренно «тупой» layout — ноль знания о
// сущностях, весь контент приходит слотами-ReactNode.
export function EntityPageLayout<V extends string>({
  header,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel,
  rail,
  railTitle,
}: EntityPageLayoutProps<V>) {
  const active = tabs.find((t) => t.value === activeTab)

  return (
    // Высота-lock на широких (xl): колонки со своим скроллом. min-h (не h) —
    // высокая шапка (inline-правка) не сжимает контент в ноль, а растит страницу,
    // и её скроллит <main> в app-shell.
    <div className="flex flex-col gap-4 p-4 md:p-6 xl:min-h-full">
      <div className="shrink-0 rounded-card bg-bg-elevated p-5 ring-1 ring-inset ring-border-subtle md:p-6">
        {header}
      </div>

      <div
        className={cn(
          'grid min-h-0 grid-cols-1 gap-4 xl:flex-1',
          rail != null && 'xl:grid-cols-[minmax(0,1fr)_360px]',
        )}
      >
        <section className="flex min-w-0 flex-col overflow-hidden rounded-card bg-bg-elevated ring-1 ring-inset ring-border-subtle xl:min-h-0">
          <div className="shrink-0 border-b border-border-subtle p-3">
            <Tabs
              items={tabs.map(({ value, label, badge }) => ({
                value,
                label,
                ...(badge != null ? { badge } : {}),
              }))}
              value={activeTab}
              onChange={onTabChange}
              aria-label={tabsLabel ?? 'Разделы'}
            />
          </div>

          <div className="p-4 md:p-5 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
            {active?.content}
          </div>
        </section>

        {rail != null && (
          <aside
            aria-label={railTitle ?? 'Панель'}
            className="flex min-h-0 flex-col overflow-hidden rounded-card bg-bg-elevated ring-1 ring-inset ring-border-subtle"
          >
            {railTitle != null && (
              <div className="shrink-0 border-b border-border-subtle p-3">
                <span className="label">{railTitle}</span>
              </div>
            )}
            {/* Скролл ленты на всех брейкпоинтах: на xl рельс height-locked
                (flex-1 + min-h-0), на узких высоту ограничивает max-h в vh. */}
            <div className="max-h-[70vh] min-h-0 flex-1 overflow-y-auto p-4 xl:max-h-none">
              {rail}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}
